import { useCallback, useEffect, useRef, useState } from 'react'

import {
  chatApi,
  type ActivityChatSummary,
  type ActivityDetail,
  type ActivityMessage,
} from '../api'
import {
  isActivityMessage,
  mergeActivityMessage,
  mergeActivityTimeline,
} from '../format'
import { useChatRealtime } from './use-chat-realtime'

export type ActivityChatPhase = 'loading' | 'ready' | 'error'

export interface ActivityChatState {
  activity: ActivityDetail | null
  summary: ActivityChatSummary | null
  messages: ActivityMessage[]
  phase: ActivityChatPhase
  refreshing: boolean
  error: unknown
  refresh: () => void
  setMessages: (updater: (items: ActivityMessage[]) => ActivityMessage[]) => void
  markRead: (messageId: string) => Promise<void>
}

/**
 * Activity chat route state: detail metadata, frozen timeline (user + system
 * join items) and the best-effort chat list summary (pin/unread metadata).
 * The summary is optional so a chat still opens when the list endpoint fails.
 */
export function useActivityChat(activityId: string | null): ActivityChatState {
  const [activity, setActivity] = useState<ActivityDetail | null>(null)
  const [summary, setSummary] = useState<ActivityChatSummary | null>(null)
  const [messages, setMessagesState] = useState<ActivityMessage[]>([])
  const [phase, setPhase] = useState<ActivityChatPhase>('loading')
  const [refreshing, setRefreshing] = useState(false)
  const [error, setError] = useState<unknown>(null)
  const [request, setRequest] = useState(0)
  const loadedRef = useRef(false)
  const inFlightRef = useRef(false)
  const lastReadRef = useRef<string | null>(null)
  const readInFlightRef = useRef(false)
  const mutationRef = useRef(0)

  useEffect(() => {
    lastReadRef.current = null
  }, [activityId])

  useEffect(() => {
    if (!activityId) return
    const controller = new AbortController()
    const mutationAtStart = mutationRef.current
    inFlightRef.current = true
    if (!loadedRef.current) setPhase('loading')

    Promise.allSettled([
      chatApi.getActivity(activityId, { signal: controller.signal }),
      chatApi.listActivityMessages(activityId, {
        signal: controller.signal,
      }),
      chatApi.listActivityChats({ signal: controller.signal }),
    ])
      .then(([activityResult, messagesResult, chatsResult]) => {
        if (controller.signal.aborted) return
        const detail =
          activityResult.status === 'fulfilled' ? activityResult.value : null
        const timeline =
          messagesResult.status === 'fulfilled' ? messagesResult.value : null

        if (!detail || !timeline) {
          setError(
            activityResult.status === 'rejected'
              ? activityResult.reason
              : messagesResult.status === 'rejected'
                ? messagesResult.reason
                : new Error('Activity chat unavailable'),
          )
          setRefreshing(false)
          if (!loadedRef.current) setPhase('error')
          return
        }

        const list = chatsResult.status === 'fulfilled' ? chatsResult.value : null
        const nextSummary =
          list?.items.find((item) => item.id === activityId) ?? null

        setActivity(detail)
        if (mutationRef.current === mutationAtStart) {
          setMessagesState(timeline.items)
        } else {
          setMessagesState((current) =>
            mergeActivityTimeline(timeline.items, current),
          )
        }
        setSummary(nextSummary)
        loadedRef.current = true
        setPhase('ready')
        setRefreshing(false)
        setError(null)
        inFlightRef.current = false
      })
      .catch((cause: unknown) => {
        if (controller.signal.aborted) return
        setError(cause)
        setRefreshing(false)
        inFlightRef.current = false
        if (!loadedRef.current) setPhase('error')
      })

    return () => controller.abort()
  }, [activityId, request])

  const refresh = useCallback(() => {
    setError(null)
    if (loadedRef.current) setRefreshing(true)
    setRequest((value) => value + 1)
  }, [])

  const sync = useCallback(() => {
    if (inFlightRef.current) return
    setRequest((value) => value + 1)
  }, [])

  const appendRealtimeMessage = useCallback(
    (_activityId: string, message: unknown): boolean => {
      if (!isActivityMessage(message)) return false
      mutationRef.current += 1
      setMessagesState((current) => mergeActivityMessage(current, message))
      return true
    },
    [],
  )

  useChatRealtime({
    channel: 'activity',
    id: activityId,
    onUpdate: sync,
    onActivityMessage: appendRealtimeMessage,
  })

  const markRead = useCallback(
    async (messageId: string) => {
      if (!activityId || !messageId) return
      if (lastReadRef.current === messageId || readInFlightRef.current) return
      readInFlightRef.current = true
      lastReadRef.current = messageId
      try {
        await chatApi.readActivityMessages(activityId, messageId)
      } catch {
        // Keep the cursor unread server-side; the next visible tick retries.
        if (lastReadRef.current === messageId) lastReadRef.current = null
      } finally {
        readInFlightRef.current = false
      }
    },
    [activityId],
  )

  const setMessages = useCallback(
    (updater: (items: ActivityMessage[]) => ActivityMessage[]) => {
      mutationRef.current += 1
      setMessagesState(updater)
    },
    [],
  )

  return {
    activity,
    summary,
    messages,
    phase,
    refreshing,
    error,
    refresh,
    setMessages,
    markRead,
  }
}
